export const fallbackPlans = [
  {
    id: 'hyrox-foundation',
    name: 'Hyrox Foundation',
    sport: 'hybrid',
    goal: 'Hybrid engine + strength endurance',
    summary: '4-week build blending running, sled work, and stations.',
    defaultFocus: 'Race Prep',
    durationTarget: 60,
    distanceTarget: 8,
    outline: [
      { week: 'Week 1', sessions: ['Run 40 min', 'Sled push/pull technique', 'Burpee broad jumps'] },
      { week: 'Week 2', sessions: ['Tempo 20 min', 'Wall balls + farmer carry', 'Row intervals'] },
      { week: 'Week 3', sessions: ['Mixed station circuit', 'Run 50 min', 'Ski erg intervals'] },
      { week: 'Week 4', sessions: ['Race simulation', 'Recovery 30 min', 'Mobility reset'] },
    ],
    source: 'fallback',
  },
  {
    id: 'hybrid-endurance',
    name: 'Hybrid Endurance',
    sport: 'hybrid',
    goal: 'Strength base + endurance conditioning',
    summary: 'Balanced week with strength intervals and longer cardio blocks.',
    defaultFocus: 'Base',
    durationTarget: 55,
    distanceTarget: 6,
    outline: [
      { week: 'Week 1', sessions: ['Strength density 30 min', 'Zone 2 35 min', 'Recovery 25 min'] },
      { week: 'Week 2', sessions: ['Tempo 15 min', 'Carry + sled circuit', 'Endurance 50 min'] },
      { week: 'Week 3', sessions: ['Speed ladder', 'Zone 2 45 min', 'Mobility reset'] },
      { week: 'Week 4', sessions: ['Test day', 'Recovery 30 min', 'Endurance 60 min'] },
    ],
    source: 'fallback',
  },
  {
    id: 'running-pace-builder',
    name: 'Running Pace Builder',
    sport: 'running',
    goal: 'Build aerobic base and controlled speed',
    summary: '4-week progression for pacing confidence and recovery balance.',
    defaultFocus: 'Base',
    durationTarget: 50,
    distanceTarget: 8,
    outline: [
      { week: 'Week 1', sessions: ['Base run 40 min', 'Tempo 15 min', 'Recovery jog 25 min'] },
      { week: 'Week 2', sessions: ['Intervals 6x2 min', 'Base run 45 min', 'Mobility + strides'] },
      { week: 'Week 3', sessions: ['Tempo 20 min', 'Long run 60 min', 'Recovery jog 30 min'] },
      { week: 'Week 4', sessions: ['Test 5K effort', 'Easy run 35 min', 'Reset + mobility'] },
    ],
    source: 'fallback',
  },
  {
    id: 'cycling-power-base',
    name: 'Cycling Power Base',
    sport: 'cycling',
    goal: 'Raise threshold while keeping endurance volume',
    summary: 'Balanced cadence, hill, and recovery work over 4 weeks.',
    defaultFocus: 'Tempo',
    durationTarget: 60,
    distanceTarget: 22,
    outline: [
      { week: 'Week 1', sessions: ['Zone 2 ride 60 min', 'Cadence drill 40 min', 'Recovery spin 25 min'] },
      { week: 'Week 2', sessions: ['Hill repeats 6x3 min', 'Tempo ride 30 min', 'Easy spin 30 min'] },
      { week: 'Week 3', sessions: ['Power intervals 5x4 min', 'Zone 2 ride 75 min', 'Mobility reset'] },
      { week: 'Week 4', sessions: ['Threshold test ride', 'Easy spin 35 min', 'Recovery day'] },
    ],
    source: 'fallback',
  },
  {
    id: 'swim-tech-endurance',
    name: 'Swim Tech Endurance',
    sport: 'swimming',
    goal: 'Improve efficiency and repeatable pace',
    summary: 'Technique-first blocks with steady endurance progression.',
    defaultFocus: 'Base',
    durationTarget: 45,
    distanceTarget: 2,
    outline: [
      { week: 'Week 1', sessions: ['Drill set 30 min', 'Steady swim 25 min', 'Kick + pull mix'] },
      { week: 'Week 2', sessions: ['Tempo 12x50m', 'Technique 30 min', 'Recovery easy laps'] },
      { week: 'Week 3', sessions: ['Pace set 8x100m', 'Steady swim 35 min', 'Breath control set'] },
      { week: 'Week 4', sessions: ['400m time trial', 'Easy technique swim', 'Mobility + recovery'] },
    ],
    source: 'fallback',
  },
  {
    id: 'trail-climb-engine',
    name: 'Trail Climb Engine',
    sport: 'trail',
    goal: 'Climbing strength and downhill control',
    summary: 'Trail-focused build with elevation, tempo, and durability work.',
    defaultFocus: 'Race Prep',
    durationTarget: 65,
    distanceTarget: 10,
    outline: [
      { week: 'Week 1', sessions: ['Hill reps 8x60s', 'Easy trail 35 min', 'Hike strength 40 min'] },
      { week: 'Week 2', sessions: ['Tempo trail 25 min', 'Long trail 70 min', 'Recovery walk + mobility'] },
      { week: 'Week 3', sessions: ['Climb repeats 6x3 min', 'Trail endurance 80 min', 'Easy trail 30 min'] },
      { week: 'Week 4', sessions: ['Simulation run', 'Recovery 30 min', 'Mobility reset'] },
    ],
    source: 'fallback',
  },
];

export const emptyPlan = {
  name: '',
  sport: 'running',
  goal: '',
  summary: '',
  defaultFocus: 'Base',
  durationTarget: '',
  distanceTarget: '',
  outline: [{ week: 'Week 1', sessions: [''] }],
};

export const TRAINING_WALKTHROUGH_STEPS = [
  {
    id: 'pick_world',
    title: 'Pick a Training Category',
    what: 'Choose a category like Hybrid, Running, Cycling, Swim, or Trail to filter plans by sport.',
    why: 'The category keeps your plan list focused so sessions match your current goal.',
    firstAction: 'Select a training category.',
  },
  {
    id: 'pick_plan',
    title: 'Pick a Plan',
    what: 'Open Plan Library and select one plan to preview weekly sessions.',
    why: 'A selected plan powers checklist context and timer flow.',
    firstAction: 'Open and select a plan.',
  },
  {
    id: 'create_plan',
    title: 'Create or Edit a Plan',
    what: 'Use Create plan to build or adjust a program structure that fits your week.',
    why: 'Custom plans let you adapt volume and focus without losing consistency.',
    firstAction: 'Open Create plan.',
  },
  {
    id: 'start_session',
    title: 'Run the Session Timer',
    what: 'Start your session from the selected plan and log it with reflections.',
    why: 'Timer + completion logging keeps training data clean for reports and XP.',
    firstAction: 'Start a session timer.',
  },
];

export const worldFallbackPlans = [
  {
    id: 'hybrid-threshold-builder',
    name: 'Hybrid Threshold Builder',
    sport: 'hybrid',
    goal: 'Raise threshold and station repeatability',
    summary: 'Race-specific pacing under fatigue over 4 weeks.',
    defaultFocus: 'Tempo',
    durationTarget: 58,
    distanceTarget: 7,
    outline: [
      { week: 'Week 1', sessions: ['Tempo run 20 min', 'Sled push intervals', 'Row + carry combo'] },
      { week: 'Week 2', sessions: ['Wall balls density', 'Run 45 min', 'Burpee broad jumps'] },
      { week: 'Week 3', sessions: ['Station circuit 5 rounds', 'Tempo 25 min', 'Recovery mobility'] },
      { week: 'Week 4', sessions: ['Simulation effort', 'Easy zone 2 35 min', 'Reset day'] },
    ],
    source: 'fallback',
  },
  {
    id: 'hybrid-race-primer',
    name: 'Hybrid Race Primer',
    sport: 'hybrid',
    goal: 'Peak for event day confidence',
    summary: 'Short focused block to sharpen transitions and pace control.',
    defaultFocus: 'Race Prep',
    durationTarget: 52,
    distanceTarget: 6,
    outline: [
      { week: 'Week 1', sessions: ['Run + ski erg bricks', 'Farmer carry ladders', 'Recovery spin 30 min'] },
      { week: 'Week 2', sessions: ['Wall balls + row repeats', 'Tempo run 30 min', 'Mobility reset'] },
      { week: 'Week 3', sessions: ['Mini simulation', 'Easy endurance 40 min', 'Drill + transitions'] },
      { week: 'Week 4', sessions: ['Race tune-up', 'Easy shakeout', 'Event prep'] },
    ],
    source: 'fallback',
  },
  {
    id: 'running-5k-blueprint',
    name: 'Running 5K Blueprint',
    sport: 'running',
    goal: 'Build speed and pacing for 5K',
    summary: 'Simple 4-week build for controlled 5K performance.',
    defaultFocus: 'Speed',
    durationTarget: 42,
    distanceTarget: 5,
    outline: [
      { week: 'Week 1', sessions: ['Strides 10x20s', 'Tempo 15 min', 'Recovery 25 min'] },
      { week: 'Week 2', sessions: ['Intervals 8x400m', 'Base run 35 min', 'Mobility + drills'] },
      { week: 'Week 3', sessions: ['Threshold 18 min', 'Long easy 50 min', 'Recovery jog 25 min'] },
      { week: 'Week 4', sessions: ['5K test', 'Easy run 30 min', 'Reset day'] },
    ],
    source: 'fallback',
  },
  {
    id: 'running-10k-steady-build',
    name: 'Running 10K Steady Build',
    sport: 'running',
    goal: 'Progress to a confident 10K',
    summary: 'Balanced endurance and tempo progression for 10K prep.',
    defaultFocus: 'Base',
    durationTarget: 58,
    distanceTarget: 10,
    outline: [
      { week: 'Week 1', sessions: ['Base run 45 min', 'Tempo 20 min', 'Recovery 30 min'] },
      { week: 'Week 2', sessions: ['Intervals 5x1k', 'Easy run 40 min', 'Long run 70 min'] },
      { week: 'Week 3', sessions: ['Tempo 25 min', 'Base run 50 min', 'Recovery jog 30 min'] },
      { week: 'Week 4', sessions: ['10K simulation', 'Easy run 35 min', 'Mobility reset'] },
    ],
    source: 'fallback',
  },
  {
    id: 'running-marathon-foundation',
    name: 'Running Marathon Foundation',
    sport: 'running',
    goal: 'Introduce 42K base structure',
    summary: 'Entry marathon base with long-run rhythm and recovery control.',
    defaultFocus: 'Base',
    durationTarget: 75,
    distanceTarget: 42,
    outline: [
      { week: 'Week 1', sessions: ['Base run 50 min', 'Tempo 20 min', 'Long run 90 min'] },
      { week: 'Week 2', sessions: ['Intervals 6x800m', 'Easy run 45 min', 'Long run 100 min'] },
      { week: 'Week 3', sessions: ['Steady run 60 min', 'Recovery jog 30 min', 'Long run 110 min'] },
      { week: 'Week 4', sessions: ['Progression run', 'Easy run 40 min', 'Mobility + reset'] },
    ],
    source: 'fallback',
  },
  {
    id: 'cycling-endurance-foundation',
    name: 'Cycling Endurance Foundation',
    sport: 'cycling',
    goal: 'Build repeatable aerobic power',
    summary: 'Steady base block for stronger week-to-week volume.',
    defaultFocus: 'Base',
    durationTarget: 70,
    distanceTarget: 28,
    outline: [
      { week: 'Week 1', sessions: ['Zone 2 70 min', 'Cadence drills 35 min', 'Recovery spin 25 min'] },
      { week: 'Week 2', sessions: ['Tempo 25 min', 'Zone 2 80 min', 'Mobility reset'] },
      { week: 'Week 3', sessions: ['Hill repeats 5x5 min', 'Endurance 90 min', 'Easy spin 30 min'] },
      { week: 'Week 4', sessions: ['Threshold check', 'Zone 2 60 min', 'Recovery day'] },
    ],
    source: 'fallback',
  },
  {
    id: 'cycling-climb-focus',
    name: 'Cycling Climb Focus',
    sport: 'cycling',
    goal: 'Improve climbing cadence and torque',
    summary: 'Hill-specific sessions with recovery balance.',
    defaultFocus: 'Tempo',
    durationTarget: 62,
    distanceTarget: 20,
    outline: [
      { week: 'Week 1', sessions: ['Hill repeats 6x3 min', 'Easy spin 35 min', 'Cadence drills'] },
      { week: 'Week 2', sessions: ['Tempo climb 20 min', 'Zone 2 70 min', 'Recovery spin'] },
      { week: 'Week 3', sessions: ['Over-under climbs', 'Long endurance 85 min', 'Mobility'] },
      { week: 'Week 4', sessions: ['Climb benchmark', 'Easy spin 30 min', 'Reset'] },
    ],
    source: 'fallback',
  },
  {
    id: 'cycling-threshold-ramp',
    name: 'Cycling Threshold Ramp',
    sport: 'cycling',
    goal: 'Lift sustainable race pace',
    summary: 'Progressive threshold work across 4 weeks.',
    defaultFocus: 'Race Prep',
    durationTarget: 64,
    distanceTarget: 24,
    outline: [
      { week: 'Week 1', sessions: ['Threshold 3x8 min', 'Zone 2 60 min', 'Recovery spin 25 min'] },
      { week: 'Week 2', sessions: ['Threshold 2x12 min', 'Tempo 30 min', 'Easy spin'] },
      { week: 'Week 3', sessions: ['Threshold 3x10 min', 'Endurance 80 min', 'Mobility'] },
      { week: 'Week 4', sessions: ['Race pace simulation', 'Easy ride 35 min', 'Recovery day'] },
    ],
    source: 'fallback',
  },
  {
    id: 'swim-sprint-control',
    name: 'Swim Sprint Control',
    sport: 'swimming',
    goal: 'Improve top-end speed mechanics',
    summary: 'Short sprint sets with strict form quality.',
    defaultFocus: 'Speed',
    durationTarget: 40,
    distanceTarget: 1.5,
    outline: [
      { week: 'Week 1', sessions: ['8x25m sprint', 'Technique drills 25 min', 'Recovery swim'] },
      { week: 'Week 2', sessions: ['10x25m sprint', 'Tempo 10x50m', 'Kick set'] },
      { week: 'Week 3', sessions: ['12x25m sprint', 'Steady 30 min', 'Breath control'] },
      { week: 'Week 4', sessions: ['Sprint test set', 'Easy technique', 'Mobility reset'] },
    ],
    source: 'fallback',
  },
  {
    id: 'swim-distance-builder',
    name: 'Swim Distance Builder',
    sport: 'swimming',
    goal: 'Extend steady-distance capacity',
    summary: 'Longer continuous sets with controlled pacing.',
    defaultFocus: 'Base',
    durationTarget: 50,
    distanceTarget: 2.5,
    outline: [
      { week: 'Week 1', sessions: ['Steady 30 min', 'Pull set 6x100m', 'Easy recovery'] },
      { week: 'Week 2', sessions: ['Steady 35 min', 'Pace 8x100m', 'Kick + drill'] },
      { week: 'Week 3', sessions: ['Steady 40 min', 'Threshold 5x200m', 'Recovery laps'] },
      { week: 'Week 4', sessions: ['Distance test', 'Easy technique', 'Reset'] },
    ],
    source: 'fallback',
  },
  {
    id: 'swim-race-readiness',
    name: 'Swim Race Readiness',
    sport: 'swimming',
    goal: 'Prepare for race pacing and control',
    summary: 'Race-pace reps and sharpened form work.',
    defaultFocus: 'Race Prep',
    durationTarget: 48,
    distanceTarget: 2,
    outline: [
      { week: 'Week 1', sessions: ['Race pace 10x100m', 'Technique 25 min', 'Recovery'] },
      { week: 'Week 2', sessions: ['Race pace 6x200m', 'Easy swim 30 min', 'Drill set'] },
      { week: 'Week 3', sessions: ['Broken race set', 'Steady 35 min', 'Mobility'] },
      { week: 'Week 4', sessions: ['Time trial', 'Easy flush', 'Reset day'] },
    ],
    source: 'fallback',
  },
  {
    id: 'trail-5k-starter',
    name: 'Trail 5K Starter',
    sport: 'trail',
    goal: 'Simple entry trail 5K structure',
    summary: 'Beginner-friendly trail rhythm with climb control.',
    defaultFocus: 'Base',
    durationTarget: 46,
    distanceTarget: 5,
    outline: [
      { week: 'Week 1', sessions: ['Trail easy 30 min', 'Hill reps 6x45s', 'Recovery walk'] },
      { week: 'Week 2', sessions: ['Tempo trail 18 min', 'Trail run 35 min', 'Mobility'] },
      { week: 'Week 3', sessions: ['Hill reps 8x45s', 'Trail endurance 45 min', 'Recovery jog'] },
      { week: 'Week 4', sessions: ['5K trail effort', 'Easy trail 25 min', 'Reset'] },
    ],
    source: 'fallback',
  },
  {
    id: 'trail-10k-builder',
    name: 'Trail 10K Builder',
    sport: 'trail',
    goal: 'Build durable trail 10K output',
    summary: 'Elevation and tempo progression for 10K efforts.',
    defaultFocus: 'Tempo',
    durationTarget: 62,
    distanceTarget: 10,
    outline: [
      { week: 'Week 1', sessions: ['Trail base 45 min', 'Hill repeats 6x2 min', 'Recovery walk'] },
      { week: 'Week 2', sessions: ['Tempo trail 25 min', 'Long trail 80 min', 'Mobility'] },
      { week: 'Week 3', sessions: ['Climb repeats 7x2 min', 'Trail base 55 min', 'Recovery jog'] },
      { week: 'Week 4', sessions: ['10K trail simulation', 'Easy trail 35 min', 'Reset'] },
    ],
    source: 'fallback',
  },
  {
    id: 'trail-ultra-base',
    name: 'Trail Ultra Base',
    sport: 'trail',
    goal: 'Long-form mountain endurance',
    summary: 'Long climbs, controlled descents, and recovery rhythm.',
    defaultFocus: 'Race Prep',
    durationTarget: 85,
    distanceTarget: 42,
    outline: [
      { week: 'Week 1', sessions: ['Long trail 95 min', 'Hill reps 8x90s', 'Recovery hike'] },
      { week: 'Week 2', sessions: ['Steady trail 70 min', 'Tempo climb 22 min', 'Mobility'] },
      { week: 'Week 3', sessions: ['Long trail 110 min', 'Easy trail 40 min', 'Recovery walk'] },
      { week: 'Week 4', sessions: ['Ultra simulation block', 'Easy flush', 'Reset day'] },
    ],
    source: 'fallback',
  },
];

export const focusOptions = ['Base', 'Tempo', 'Speed', 'Recovery', 'Race Prep'];
export const sports = ['running', 'cycling', 'swimming', 'hybrid', 'trail'];
export const trainingWorlds = [
  { id: 'hybrid', title: 'Hybrid Arena (Hybrid Training)', subtitle: 'Carry, sled, engine', sport: 'hybrid' },
  { id: 'running', title: 'Velocity Lab (Running)', subtitle: 'Tempo and pace craft', sport: 'running' },
  { id: 'cycling', title: 'Torque Studio (Cycling)', subtitle: 'Cadence and climbs', sport: 'cycling' },
  { id: 'swimming', title: 'Flow Pool (Swimming)', subtitle: 'Form and oxygen', sport: 'swimming' },
  { id: 'trail', title: 'Trail Forge (Trail Running)', subtitle: 'Elevation resilience', sport: 'trail' },
];

export const worldShortLabels = {
  hybrid: 'Hybrid Arena',
  running: 'Velocity Lab',
  cycling: 'Torque Studio',
  swimming: 'Flow Pool',
  trail: 'Trail Forge',
};

export const outlinePresets = {
  running: {
    Base: ['Base run 40 min', 'Tempo 20 min', 'Recovery 30 min'],
    Tempo: ['Tempo 20 min', 'Intervals 6x2', 'Easy run 35 min'],
    Speed: ['Strides 10x20s', 'Intervals 8x400', 'Recovery 25 min'],
    Recovery: ['Easy run 30 min', 'Mobility reset', 'Recovery walk 20 min'],
    'Race Prep': ['Race simulation', 'Tempo 25 min', 'Easy run 30 min'],
  },
  cycling: {
    Base: ['Zone 2 60 min', 'Cadence 45 min', 'Recovery spin 30 min'],
    Tempo: ['Tempo 20 min', 'Hill repeats 6x2', 'Easy spin 30 min'],
    Speed: ['Power sprints 8x30s', 'Intervals 5x3', 'Recovery 25 min'],
    Recovery: ['Easy spin 30 min', 'Mobility reset', 'Core reset'],
    'Race Prep': ['Test ride 45 min', 'Tempo 30 min', 'Easy spin 35 min'],
  },
  swimming: {
    Base: ['Technique 30 min', 'Steady 25 min', 'Kick set 20 min'],
    Tempo: ['Tempo 20 min', 'Intervals 10x50', 'Recovery 20 min'],
    Speed: ['Sprints 12x25', 'Pull buoy 25 min', 'Recovery 20 min'],
    Recovery: ['Easy swim 25 min', 'Mobility reset', 'Breath control'],
    'Race Prep': ['Test 400m', 'Tempo 25 min', 'Easy 20 min'],
  },
  hybrid: {
    Base: ['Carry ladder 20 min', 'Zone 2 35 min', 'Recovery 25 min'],
    Tempo: ['Mixed circuit 30 min', 'Tempo run 25 min', 'Mobility reset'],
    Speed: ['Sled push 6x20m', 'Row 5x500', 'Recovery 25 min'],
    Recovery: ['Easy run 25 min', 'Mobility reset', 'Breathwork'],
    'Race Prep': ['Simulation day', 'Tempo 20 min', 'Recovery 30 min'],
  },
  trail: {
    Base: ['Hills 6x60s', 'Easy run 30 min', 'Strength hike 40 min'],
    Tempo: ['Tempo trail 25 min', 'Long trail 75 min', 'Mobility reset'],
    Speed: ['Hill sprints 10x20', 'Easy run 30 min', 'Recovery 25 min'],
    Recovery: ['Easy trail 30 min', 'Mobility reset', 'Recovery walk'],
    'Race Prep': ['Trail simulation', 'Tempo 25 min', 'Recovery 30 min'],
  },
};
